import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getMensagensService } from '../api/mensagensService';
import { MESSAGE_FILTERS, MESSAGE_STATUS, PERFORMANCE } from '../utils/constants';
import {
  formatConversationTime,
  formatMessagePreview,
  formatUnreadCount,
  formatContactName,
} from '../utils/formatters';

/**
 * useConversacoes
 * --------------------------------------------------------------------------
 * Reglas de oro respetadas:
 * - loadData es una función async normal (NO useCallback) -> sin dependencias
 *   circulares con useState.
 * - El useEffect de carga inicial NO depende de loadData ni de ningún estado
 *   "initialized". Corre una sola vez al montar (re-ejecuta solo si cambia
 *   el userId resuelto).
 * - mountedRef y loadingRef son useRef, no useState (no disparan renders ni
 *   generan dependencias).
 * - mountedRef se resetea a true EN CADA ejecución del efecto de montaje,
 *   para sobrevivir al doble mount/unmount de React 18 StrictMode en
 *   desarrollo. Este era el bug raíz del deadlock original.
 * - Si hay error, loading siempre pasa a false (finally + catch).
 */
export function useConversacoes({
  initialFilter = MESSAGE_FILTERS.ALL,
  initialSearch = '',
  pollingInterval = PERFORMANCE.STALE_TIME_MS,
  autoLoad = true,
  userId: propUserId,
} = {}) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true); // arranca en true: aún no cargamos nada
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(initialSearch);
  const [filter, setFilter] = useState(initialFilter);

  // --- refs (no useState) ---
  const mountedRef = useRef(false);
  const loadingRef = useRef(false);
  const pollingRef = useRef(null);
  const serviceRef = useRef(null);
  const hasLoadedRef = useRef(false);

  // --- userId resuelto (memo puro, no afecta el ciclo de carga) ---
  const resolvedUserId = useMemo(() => {
    if (propUserId) return propUserId;
    try {
      const s = localStorage.getItem('auth_user');
      if (s) {
        const parsed = JSON.parse(s);
        if (parsed?.id) return parsed.id;
      }
    } catch (e) {
      console.warn('[useConversacoes] No se pudo parsear auth_user:', e.message);
    }
    try {
      const d = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      if (d.users?.[0]?.id) return d.users[0].id;
    } catch (e) {
      console.warn('[useConversacoes] No se pudo parsear imoveispro_db:', e.message);
    }
    return null;
  }, [propUserId]);

  // --- loadData: función async normal, SIN useCallback ---
  // No depende de ningún estado que ella misma modifique. Lee desde refs y
  // escribe en setState directamente; React batchea esos setState.
  const loadData = async (isRefresh = false) => {
    if (loadingRef.current) {
      console.log('[useConversacoes] loadData ignorado: ya hay una carga en curso');
      return;
    }
    if (!mountedRef.current) {
      console.log('[useConversacoes] loadData ignorado: componente no montado');
      return;
    }

    loadingRef.current = true;
    console.log('[useConversacoes] Iniciando carga', { isRefresh });

    if (isRefresh) {
      setRefreshing(true);
    } else if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const svc = serviceRef.current;
      if (!svc) {
        throw new Error('Servicio de mensagens no está listo');
      }

      // Clientes
      let clients = [];
      try {
        const ctx = await svc._getContext();
        if (ctx?.collections?.clients?.read) {
          const raw = await ctx.collections.clients.read();
          clients = Array.isArray(raw) ? raw : [];
        }
      } catch (e) {
        console.warn('[useConversacoes] Error leyendo clients:', e.message);
      }

      // Mensajes agrupados por cliente
      let messagesByClient = {};
      try {
        messagesByClient = (await svc.getMessagesByClient()) || {};
      } catch (e) {
        console.warn('[useConversacoes] Error leyendo getMessagesByClient:', e.message);
      }

      const list = [];
      const clientsWithMsgs = new Set(Object.keys(messagesByClient));

      // Conversaciones con mensajes
      for (const [clientId, msgs] of Object.entries(messagesByClient)) {
        if (!Array.isArray(msgs) || msgs.length === 0) continue;
        const last = msgs[0];
        const c =
          clients.find((x) => x.id === clientId) || {
            id: clientId,
            name: last.clientName || 'Cliente',
            phone: last.clientPhone || '',
          };

        list.push({
          id: clientId,
          clientId,
          clientName: c.name || 'Cliente',
          clientPhone: c.phone || '',
          lastMessage: {
            id: last.id,
            body: last.message || '',
            direction: last.direction || 'outbound',
            status: last.status || MESSAGE_STATUS?.SENT || 'sent',
            timestamp: last.createdAt || 0,
          },
          unreadCount: msgs.filter(
            (m) => m.direction === 'inbound' && m.status !== 'read'
          ).length,
          totalMessages: msgs.length,
          lastContact: last.createdAt || 0,
          isArchived: false,
          isStarred: false,
          _messages: msgs,
          _client: c,
        });
      }

      // Clientes sin mensajes (estilo WhatsApp: igual aparecen si tienen teléfono)
      for (const c of clients) {
        if (clientsWithMsgs.has(c.id)) continue;
        const digits = (c.phone || '').replace(/\D/g, '');
        if (digits.length < 10) continue;

        list.push({
          id: c.id,
          clientId: c.id,
          clientName: c.name || 'Cliente',
          clientPhone: c.phone || '',
          lastMessage: null,
          unreadCount: 0,
          totalMessages: 0,
          lastContact: c.createdAt || 0,
          isArchived: false,
          isStarred: false,
          _messages: [],
          _client: c,
        });
      }

      list.sort((a, b) => {
        if (a.lastMessage && b.lastMessage) return b.lastContact - a.lastContact;
        if (a.lastMessage) return -1;
        if (b.lastMessage) return 1;
        return (a.clientName || '').localeCompare(b.clientName || '', 'pt-BR');
      });

      if (mountedRef.current) {
        setConversations(list);
        hasLoadedRef.current = true;
        console.log('[useConversacoes] Carga OK:', list.length, 'conversas');
      }
    } catch (err) {
      console.error('[useConversacoes] Error en loadData:', err.message);
      if (mountedRef.current) {
        setError(err.message);
        hasLoadedRef.current = true;
      }
    } finally {
      loadingRef.current = false;
      // CRÍTICO: esto SIEMPRE corre, haya éxito o error, así loading nunca
      // se queda colgado en true.
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
      console.log('[useConversacoes] loadData finalizado (loading=false)');
    }
  };

  // --- Efecto de montaje: instancia el servicio y arregla mountedRef ---
  // mountedRef se pone en true AQUÍ, en cada ejecución del efecto, no solo
  // declarado arriba. Esto es lo que lo hace inmune al doble efecto de
  // StrictMode: monta -> desmonta (pone false) -> vuelve a montar (pone
  // true de nuevo). Sin esto, mountedRef queda en false para siempre y
  // todos los `if (mountedRef.current)` de loadData se saltan -> deadlock.
  useEffect(() => {
    mountedRef.current = true;
    serviceRef.current = getMensagensService(resolvedUserId || undefined);
    console.log('[useConversacoes] Montado / servicio listo', { resolvedUserId });

    return () => {
      mountedRef.current = false;
      console.log('[useConversacoes] Desmontado (o re-render de StrictMode)');
    };
  }, [resolvedUserId]);

  // --- Carga inicial: NO depende de loadData ni de "initialized" ---
  useEffect(() => {
    if (!autoLoad) return;
    if (resolvedUserId === undefined) return; // todavía resolviendo
    if (hasLoadedRef.current) return; // ya cargó una vez, no repetir

    console.log('[useConversacoes] Disparando carga inicial');
    loadData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, resolvedUserId]);

  // --- Polling cada N ms ---
  useEffect(() => {
    if (!autoLoad || !pollingInterval || pollingInterval <= 0) return;

    pollingRef.current = setInterval(() => {
      console.log('[useConversacoes] Polling refresh');
      loadData(true);
    }, pollingInterval);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, pollingInterval]);

  // --- refresh manual expuesto a la UI ---
  const refresh = useCallback(() => {
    loadData(true);
  }, []);

  // --- Filtrado + búsqueda ---
  const filtered = useMemo(() => {
    let result = [...conversations];

    if (filter === MESSAGE_FILTERS.UNREAD) {
      result = result.filter((c) => c.unreadCount > 0);
    } else if (filter === MESSAGE_FILTERS.ARCHIVED) {
      result = result.filter((c) => c.isArchived);
    } else if (filter === MESSAGE_FILTERS.STARRED) {
      result = result.filter((c) => c.isStarred);
    } else {
      result = result.filter((c) => !c.isArchived);
    }

    if (search.trim()) {
      const t = search.trim().toLowerCase();
      const tDigits = t.replace(/\D/g, '');
      result = result.filter(
        (c) =>
          (c.clientName || '').toLowerCase().includes(t) ||
          (tDigits && (c.clientPhone || '').replace(/\D/g, '').includes(tDigits)) ||
          (c.lastMessage?.body || '').toLowerCase().includes(t)
      );
    }

    return result;
  }, [conversations, search, filter]);

  const stats = useMemo(
    () => ({
      total: conversations.length,
      unread: conversations.reduce((s, c) => s + c.unreadCount, 0),
      archived: conversations.filter((c) => c.isArchived).length,
      starred: conversations.filter((c) => c.isStarred).length,
    }),
    [conversations]
  );

  const getConversation = useCallback(
    (id) => conversations.find((c) => c.clientId === id) || null,
    [conversations]
  );

  const getConversationMessages = useCallback(async (id) => {
    const svc = serviceRef.current;
    if (!svc) return [];
    try {
      return (await svc.getMessagesByClientId(id)) || [];
    } catch (e) {
      console.warn('[useConversacoes] Error en getConversationMessages:', e.message);
      return [];
    }
  }, []);

  const formatForUI = useCallback((c) => {
    if (!c) return null;
    const has = !!c.lastMessage;
    return {
      ...c,
      displayName: formatContactName(c.clientName),
      displayTime: has ? formatConversationTime(c.lastContact) : '',
      displayPreview: has
        ? formatMessagePreview(
            { content: { body: c.lastMessage.body }, direction: c.lastMessage.direction },
            { maxLength: 50, showPrefix: true }
          )
        : 'Toque para iniciar conversa',
      displayUnread: formatUnreadCount(c.unreadCount),
      initials: (c.clientName || '?')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase(),
    };
  }, []);

  return {
    conversations: filtered,
    allConversations: conversations,
    stats,
    loading,
    refreshing,
    error,
    search,
    setSearch,
    filter,
    setFilter,
    refresh,
    getConversation,
    getConversationMessages,
    formatForUI,
    isEmpty: !loading && conversations.length === 0,
    hasUnread: stats.unread > 0,
    filterOptions: [
      { value: MESSAGE_FILTERS.ALL, label: 'Todas', count: stats.total },
      { value: MESSAGE_FILTERS.UNREAD, label: 'Não lidas', count: stats.unread },
      { value: MESSAGE_FILTERS.STARRED, label: 'Favoritas', count: stats.starred },
      { value: MESSAGE_FILTERS.ARCHIVED, label: 'Arquivadas', count: stats.archived },
    ],
  };
}

export default useConversacoes;